import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatProductDisplay } from './formatProduct';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const fmtCurrency = (n) =>
  '$' + parseFloat(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

/**
 * Generates and downloads a PDF invoice.
 *
 * @param {Object} invoice - Invoice data from either:
 *   - GET /api/admin/orders/:id/invoice  → shippingAddressLine / billingAddressLine
 *   - GET /api/wholesale/orders/:id      → shippingAddress (field name differs slightly)
 */
export async function generateInvoicePdf(invoice) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // ── Logo (optional) ──────────────────────────────────────────────────────────
  let logoDataUrl = null;
  let logoFormat  = 'JPEG';

  if (invoice.logoUrl) {
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const fullUrl = invoice.logoUrl.startsWith('http')
        ? invoice.logoUrl
        : `${API_URL}${invoice.logoUrl}`;
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      logoFormat = blob.type.includes('png') ? 'PNG' : 'JPEG';
      logoDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch {
      logoDataUrl = null; // graceful degradation — continue without logo
    }
  }

  // ── Header ──────────────────────────────────────────────────────────────────
  const textStartX = logoDataUrl ? 42 : 14;

  if (logoDataUrl) {
    doc.addImage(logoDataUrl, logoFormat, 14, 12, 20, 20); // 20×20mm square
  }

  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(33, 37, 41);
  doc.text('Jeremy Wine Co.', textStartX, 22);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  doc.text('Wholesale Division', textStartX, 29);

  // Invoice # and dates — right aligned
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(33, 37, 41);
  doc.text(`INVOICE`, pageWidth - 14, 18, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text(`#${invoice.orderID}`, pageWidth - 14, 25, { align: 'right' });
  doc.text(`Order Date: ${fmtDate(invoice.orderDate)}`, pageWidth - 14, 31, { align: 'right' });
  if (invoice.invoiceDueDate) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(200, 0, 0);
    doc.text(`Due: ${fmtDate(invoice.invoiceDueDate)}`, pageWidth - 14, 37, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80);
  }

  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(14, 42, pageWidth - 14, 42);

  // ── Bill To / Ship To ───────────────────────────────────────────────────────
  const colMid = pageWidth / 2 + 5;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(120);
  doc.text('BILL TO', 14, 50);
  doc.text('SHIP TO', colMid, 50);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(33, 37, 41);
  doc.setFontSize(9);

  let billY = 56;
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.companyName || '', 14, billY);
  doc.setFont('helvetica', 'normal');
  billY += 5;

  // Billing address (fall back to shipping)
  const billLine  = invoice.billingAddressLine  || invoice.shippingAddressLine  || invoice.shippingAddress || '';
  const billCity  = invoice.billingCity         || invoice.shippingCity   || '';
  const billState = invoice.billingState        || invoice.shippingState  || '';
  const billZip   = invoice.billingZip          || invoice.shippingZip    || '';

  if (billLine) { doc.text(billLine, 14, billY); billY += 5; }
  if (billCity) { doc.text(`${billCity}, ${billState} ${billZip}`, 14, billY); billY += 5; }
  if (invoice.email) { doc.text(invoice.email, 14, billY); billY += 5; }
  if (invoice.phone) { doc.text(invoice.phone, 14, billY); }

  // Shipping address
  const shipLine  = invoice.shippingAddressLine || invoice.shippingAddress || '';
  const shipCity  = invoice.shippingCity  || '';
  const shipState = invoice.shippingState || '';
  const shipZip   = invoice.shippingZip   || '';

  if (shipLine || shipCity) {
    let shipY = 56;
    doc.setFont('helvetica', 'bold');
    doc.text(invoice.companyName || '', colMid, shipY);
    doc.setFont('helvetica', 'normal');
    shipY += 5;
    if (shipLine) { doc.text(shipLine, colMid, shipY); shipY += 5; }
    if (shipCity) { doc.text(`${shipCity}, ${shipState} ${shipZip}`, colMid, shipY); }
  }

  // ── Line Items Table ────────────────────────────────────────────────────────
  const tableStartY = Math.max(billY + 12, 88);

  autoTable(doc, {
    startY: tableStartY,
    head: [['Product', 'Qty', 'Unit Price', 'Line Total']],
    body: (invoice.items || []).map(item => {
      const { primary, secondary } = formatProductDisplay({
        proBrand:       item.proBrand,
        proFanciful:    item.name,       // aliased in SQL
        proVintage:     item.vintage,
        proVarietal:    item.varietal,
        proVersion:     item.proVersion,
        proAppellation: item.proAppellation,
      });
      return [
        { content: primary + (secondary ? '\n' + secondary : ''), styles: { fontStyle: 'normal' } },
        item.quantity,
        fmtCurrency(item.pricePerUnit),
        fmtCurrency(item.totalPrice),
      ];
    }),
    foot: [['', '', 'Total Due', fmtCurrency(invoice.totalAmount)]],
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [33, 37, 41], textColor: [255, 255, 255], fontStyle: 'bold' },
    footStyles: { fillColor: [248, 249, 250], textColor: [33, 37, 41], fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 'auto', overflow: 'linebreak' },
      1: { halign: 'center', cellWidth: 18 },
      2: { halign: 'right',  cellWidth: 28 },
      3: { halign: 'right',  cellWidth: 28 },
    },
    alternateRowStyles: { fillColor: [250, 250, 250] },
  });

  // ── Payment Info ────────────────────────────────────────────────────────────
  const afterTable = doc.lastAutoTable.finalY + 10;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(33, 37, 41);
  doc.text('Payment Information', 14, afterTable);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80);
  let pyY = afterTable + 6;

  const ptLabel = {
    invoice:     'Invoice / Net Terms',
    credit_card: 'Credit Card',
    both:        'Invoice or Credit Card',
  }[invoice.paymentType] || invoice.paymentType || '—';

  doc.text(`Payment Type: ${ptLabel}`, 14, pyY); pyY += 5;
  doc.text(`Status: ${invoice.paymentStatus || '—'}`, 14, pyY); pyY += 5;
  if (invoice.netDays) {
    doc.text(`Terms: Net ${invoice.netDays} days`, 14, pyY); pyY += 5;
  }
  if (invoice.invoiceDueDate) {
    doc.setTextColor(invoice.invoicePaidDate ? 80 : 180, 0, 0);
    doc.text(`Due: ${fmtDate(invoice.invoiceDueDate)}`, 14, pyY);
    doc.setTextColor(80);
    pyY += 5;
  }
  if (invoice.invoicePaidDate) {
    doc.setTextColor(0, 128, 0);
    doc.text(`Paid: ${fmtDate(invoice.invoicePaidDate)}`, 14, pyY);
    doc.setTextColor(80);
  }

  // ── Footer ──────────────────────────────────────────────────────────────────
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setTextColor(160);
  doc.text('Jeremy Wine Co. — Wholesale Division', pageWidth / 2, pageHeight - 12, { align: 'center' });
  doc.text('Thank you for your business.', pageWidth / 2, pageHeight - 7, { align: 'center' });

  // ── Save ────────────────────────────────────────────────────────────────────
  doc.save(`JWC-Invoice-${invoice.orderID}.pdf`);
}
